import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import fileDownload from 'js-file-download';
import { Tooltip } from '@mui/material';
import {
	Download,
	Upload,
	ArrowLeft,
	ChevronRight,
	CheckCircle,
	XCircle,
	BookOpen,
	Circle,
	Loader2,
	RefreshCw,
	Clock3,
} from 'lucide-react';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import RegistryUploadRunService from '@Helpers/URLHelpers/WeboutService/RegistryUploadRunService';
import RegistryUploadJobService from '@Helpers/URLHelpers/WeboutService/RegistryUploadJobService';
import RegistriesImportRow from '@RegistriesRepresentationalDisplay/RegistriesImportRow';
import ErrorHandler from '@Root/ErrorHandler';
import './RegistriesImportForm.scss';

const cx = 'registriesImportForm';
const POLL_INTERVAL_MS = 3000;

const FIELD_DEFS = {
	nameCard: [
		{ label: 'Name', required: true },
		{ label: 'Phone', required: true },
		{ label: 'Area code', required: true },
		{
			label: 'Email',
			required: false,
			hint: 'You can add multiple email addresses separated by commas.',
		},
		{ label: 'Department', required: false },
		{ label: 'Address', required: false },
		{ label: 'State', required: false },
		{
			label: 'Label',
			required: false,
			hint: 'You can add multiple labels separated by commas.',
		},
	],
	labelCard: [
		{ label: 'Name', required: true },
		{ label: 'Remarks', required: false },
	],
};

const deriveRunCounts = (jobs = []) => ({
	successCount: jobs.filter((j) => j.conclusion === 'pass').length,
	failureCount: jobs.filter((j) => j.conclusion === 'fail').length,
	allDone: jobs.length > 0 && jobs.every((j) => j.status === 'completed'),
});

export default function RegistriesImportForm(props) {

	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const [registriesUploadRunList, setRegistriesUploadRunList] = useState([]);
	const [selectedRunId, setSelectedRunId] = useState(null);
	const [jobDetailsCache, setJobDetailsCache] = useState({});
	const [dragActive, setDragActive] = useState(false);
	const [importTab, setImportTab] = useState('upload');
	const pollingRefs = useRef({});
	const { activeUserId } = useReduxActiveUser();
	const { currentProjectId } = useReduxProject();
	const registryUploadRunService = new RegistryUploadRunService();
	const registryUploadJobService = new RegistryUploadJobService();
	const errorHandler = new ErrorHandler();
	const isContact = props?.registryType !== 'labelCard';
	const entityLabel = isContact ? 'Contacts' : 'Filters';
	const fieldDefs = FIELD_DEFS[props?.registryType] ?? FIELD_DEFS.nameCard;
	const selectedRun = registriesUploadRunList.find((run) => run.id === selectedRunId) || null;

	useEffect(() => {
		return () => {
			Object.values(pollingRefs.current).forEach(clearInterval);
		};
	}, []);

	useEffect(() => {
		if (importTab === 'history') {
			getTaskUploadRunLists();
		}
	}, [importTab, currentProjectId, props.registryType]);

	useEffect(() => {
		registriesUploadRunList.forEach((run) => {
			if (run.status === 'processing' && !pollingRefs.current[run.id]) {
				startPollingRun(run.id);
			}
		});
	}, [registriesUploadRunList]);

	useEffect(() => {
		if (registriesUploadRunList.length === 0) {
			setSelectedRunId(null);
			return;
		}
		const selectedStillExists = registriesUploadRunList.some(
			(run) => run.id === selectedRunId,
		);
		if (!selectedStillExists) {
			setSelectedRunId(registriesUploadRunList[0].id);
		}
	}, [registriesUploadRunList, selectedRunId]);

	const getTaskUploadRunLists = async (preferredRunId = null) => {
		setLoading(true);
		try {
			if (!props.registryType) {
				return;
			}
			let response =
				await registryUploadRunService.getRegistryUploadRunListByProjectIdAndRegistryType(
					currentProjectId,
					props.registryType,
				);
			if (response.status >= 400) {
				return;
			}
			response = JSON.parse(response.body);
			setRegistriesUploadRunList(response || []);
			if (preferredRunId) {
				const preferredRun = response?.find((run) => run.id === preferredRunId);
				if (preferredRun) {
					setSelectedRunId(preferredRunId);
				}
			} else if (selectedRunId) {
				const currentRun = response?.find((run) => run.id === selectedRunId);
				if (currentRun) {
					setSelectedRunId(selectedRunId);
				} else if (response?.length > 0) {
					setSelectedRunId(response[0].id);
				}
			} else if (response?.length > 0) {
				setSelectedRunId(response[0].id);
			}
		} catch (err) {
			errorHandler.handleError(err);
		} finally {
			setLoading(false);
		}
	};

	const pollRunJobs = useCallback(async (runId) => {
		try {
			let response =
				await registryUploadJobService.getRegistryUploadJobListByRunId(runId);
			if (response.status >= 400) {
				return;
			}
			const jobs = JSON.parse(response.body) || [];
			const { successCount, failureCount, allDone } = deriveRunCounts(jobs);
			setRegistriesUploadRunList((prev) =>
				prev.map((run) => {
					if (run.id !== runId) {
						return run;
					}
					return {
						...run,
						successCount,
						failureCount,
						...(allDone
							? {
									status: 'completed',
								}
							: {}),
					};
				}),
			);
			setJobDetailsCache((prev) => ({
				...prev,
				[runId]: jobs,
			}));
			if (allDone) {
				stopPollingRun(runId);
			}
		} catch (err) {
			stopPollingRun(runId);
			errorHandler.handleError(err);
		}
	}, []);

	const startPollingRun = (runId) => {
		if (pollingRefs.current[runId]) {
			return;
		}
		pollRunJobs(runId);
		const intervalId = setInterval(() => {
			pollRunJobs(runId);
		}, POLL_INTERVAL_MS);
		pollingRefs.current[runId] = intervalId;
	};

	const stopPollingRun = (runId) => {
		if (pollingRefs.current[runId]) {
			clearInterval(pollingRefs.current[runId]);
			delete pollingRefs.current[runId];
		}
	};

	const handleJobDetailsCache = (runId, jobs) => {
		setJobDetailsCache((prev) => ({
			...prev,
			[runId]: jobs,
		}));
	};

	const handleFileUpload = async (file) => {
		if (!file) {
			return;
		}
		setFileUploading(true);
		try {
			const base64 = await convertFileToBase64(file);
			const payload = {
				file: base64,
				fileName: file.name,
				fileType: file.type,
				projectId: currentProjectId,
				registryType: props?.registryType ?? 'nameCard',
				reporterUserId: activeUserId,
				runSettings: '{}',
			};
			let response =
				await registryUploadRunService.uploadBulkRegistries(payload);
			if (response.status < 400) {
				SnackbarManager.show('Successfully imported', 'success');
				const newRun = JSON.parse(response.body);
				await new Promise((resolve) => setTimeout(resolve, 1000));
				setImportTab('history');
				await getTaskUploadRunLists(newRun.id);
			} else {
				SnackbarManager.show('Data not valid', 'error');
			}
		} catch (err) {
			errorHandler.handleError(err);
			SnackbarManager.show('Data not valid', 'error');
		} finally {
			setFileUploading(false);
		}
	};

	const convertFileToBase64 = (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (err) => reject(err);
		});

	const dataURLtoFile = (dataurl) => {
		const arr = dataurl.split(',');
		const mime = arr[0].match(/:(.*?);/)[1];
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		return new File([u8arr], 'file', { type: mime });
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(e.type === 'dragenter' || e.type === 'dragover');
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files?.length > 0) {
			handleFileUpload(e.dataTransfer.files[0]);
		}
	};

	const downloadFailureData = (runId) => {
		const run = registriesUploadRunList.find((r) => r.id === runId);
		if (run?.failureBase64Data) {
			const filename = `failure__${isContact ? 'contact' : 'label'}_data.csv`;
			fileDownload(dataURLtoFile(run.failureBase64Data), filename);
		}
	};

	const downloadSuccessData = async (runId) => {
		try {
			const bodyObj = {
				runId,
				registryType: props?.registryType ?? 'nameCard',
				conclusion: 'pass',
			};
			const response = await registryUploadJobService.exportRecords(bodyObj);
			if (response.status < 400) {
				const filename = `success__${isContact ? 'contact' : 'label'}_data.csv`;
				fileDownload(dataURLtoFile(response?.body), filename);
			}
		} catch (err) {
			errorHandler.handleError(err);
			SnackbarManager.show('Something went wrong', 'error');
		}
	};

	const downloadUploadRunFile = async (runId) => {
		try {
			let response = await registryUploadRunService.getById(runId);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				fileDownload(dataURLtoFile(response?.base64Data), response?.title);
			}
		} catch (err) {
			errorHandler.handleError(err);
			SnackbarManager.show('Something went wrong', 'error');
		}
	};

	const downloadTemplate = () => {
		let headers;
		let rows;
		if (isContact) {
			headers = [
				'Name*',
				'Email',
				'Area code',
				'Phone*',
				'Department',
				'Address',
				'State',
				'Label',
			];
			rows = [
				[
					'Rahul Joshi',
					'rahul.joshi@company.com',
					'91',
					'9876543210',
					'Tech Corp',
					'Bangalore',
					'Karnataka',
				],
				[
					'Shreya Mishra',
					'shreya.mishra@example.com',
					'91',
					'9765432109',
					'Innovation LLC',
					'Mumbai',
					'Maharashtra',
				],
				[
					'Karan Nair',
					'karan.nair@business.com',
					'91',
					'9654321098',
					'Global Solutions',
					'Delhi',
					'Delhi',
				],
			];
		} else {
			headers = ['Name*', 'Remarks'];
			rows = [
				['IT', 'General IT-related issues'],
				['Hardware', 'Physical device-related issues'],
				['Software', 'Application or system-related issues'],
			];
		}
		const csv = [headers, ...rows]
			.map((r) => r.map((c) => `"${c}"`).join(','))
			.join('\n');
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = Object.assign(document.createElement('a'), {
			href: url,
			download: isContact ? 'contacts_template.csv' : 'label_template.csv',
		});
		a.click();
		window.URL.revokeObjectURL(url);
	};

	const isProcessing = (run) => run.status === 'processing';

	const progressPercent = (run) => {
		if (!run.totalItems || run.totalItems === 0) {
			return 0;
		}
		return Math.min(
			100,
			Math.round(
				((run.successCount + run.failureCount) / run.totalItems) * 100,
			),
		);
	};

	const formatDate = (epochMs) => {
		if (!epochMs) {
			return '—';
		}
		return new Date(epochMs).toLocaleDateString();
	};

	return (
		<div className={`${cx}__page`}>
			{(fileUploading || loading) && (
				<div className={`${cx}__loaderOverlay`}>
					<div className={`${cx}__loaderSpinner`} />
					<span>
						{fileUploading
							? 'Uploading, please wait…'
							: 'Loading, please wait…'}
					</span>
				</div>
			)}
			<header className={`${cx}__pageHeader`}>
				<div className={`${cx}__pageHeaderLeft`}>
					<button className={`${cx}__backBtn`} onClick={() => navigate(-1)}>
						<ArrowLeft size={15} />
						Back
					</button>
					<div>
						<h1 className={`${cx}__pageTitle`}>Import {entityLabel}</h1>
						<p className={`${cx}__pageSubtitle`}>
							Bulk-upload {entityLabel.toLowerCase()} from a CSV or Excel file
						</p>
					</div>
				</div>
			</header>
			<main className={`${cx}__pageBody`}>
				<div className={`${cx}__tabsRow`}>
					<div className={`${cx}__tabs`}>
						<button
							className={`${cx}__tab ${importTab === 'upload' ? 'active' : ''}`}
							onClick={() => setImportTab('upload')}
						>
							<Upload size={15} />
							Upload
						</button>
						<button
							className={`${cx}__tab ${
								importTab === 'history' ? 'active' : ''
							}`}
							onClick={() => setImportTab('history')}
						>
							Import history
							{registriesUploadRunList.some(isProcessing) && (
								<span className={`${cx}__tabPulse`} />
							)}
						</button>
					</div>
					{importTab === 'history' && (
						<Tooltip title='Refresh' arrow>
							<button
								className={`${cx}__refresh`}
								onClick={() => getTaskUploadRunLists(selectedRunId)}
							>
								<RefreshCw size={14} />
								Refresh
							</button>
						</Tooltip>
					)}
				</div>
				<div className={`${cx}__content`}>
					{importTab === 'upload' && (
						<div className={`${cx}__uploadGrid`}>
							<div
								className={`${cx}__dropzone${dragActive ? ' active' : ''}`}
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
							>
								<Upload size={36} strokeWidth={1.5} />
								<h4>Drag & drop your file here</h4>
								<p>or</p>
								<label className={`${cx}__fileInput`}>
									<span>Browse files</span>
									<input
										type='file'
										accept='.xlsx,.xls,.csv'
										onChange={(e) => {
											if (e.target.files?.length > 0) {
												handleFileUpload(e.target.files[0]);
											}
										}}
									/>
								</label>
								<p className={`${cx}__fileFormats`}>
									Supports .csv, .xls, .xlsx
								</p>
							</div>
							<div className={`${cx}__instructions`}>
								<div
									className={`${cx}__instructionsHeader flex-row-center-space-between`}
								>
									<div className='flex-row-flex-start-0p5vw-gap'>
										<BookOpen size={16} />
										<h3>How to import {entityLabel.toLowerCase()}</h3>
									</div>
									<button
										className={`${cx}__templateBtn`}
										onClick={downloadTemplate}
									>
										<Download size={15} />
										Download template
									</button>
								</div>
								<div className={`${cx}__instructionsBody`}>
									<ol>
										<li>
											<strong>Prepare your file</strong> — include these
											columns:
											<ul>
												{fieldDefs.map((f) => (
													<li key={f.label} className={`${cx}__fieldRow`}>
														<Circle size={5} fill='currentColor' />
														{f.label}
														{f.required && (
															<span className={`${cx}__fieldRequired`}>
																required
															</span>
														)}
														{f.hint && (
															<div className={`${cx}__fieldHint`}>{f.hint}</div>
														)}
													</li>
												))}
											</ul>
										</li>
										<li>
											<strong>Download the template</strong> — use the header
											button to get a pre-formatted CSV.
										</li>
										<li>
											<strong>Upload your file</strong> — drag it here or click
											"Browse files."
										</li>
										<li>
											<strong>Review results</strong> — switch to Import history
											to see successes and failures.
										</li>
									</ol>
								</div>
							</div>
						</div>
					)}
					{importTab === 'history' && (
						<div className={`${cx}__historyLayout`}>
							{/* LEFT SIDE */}
							<aside className={`${cx}__historySidebar`}>
								<div className={`${cx}__sidebarHeader`}>
									<div>
										<h3>Import history</h3>
										<span>{registriesUploadRunList.length} imports</span>
									</div>
								</div>
								<div className={`${cx}__runList`}>
									{registriesUploadRunList.length === 0 ? (
										<div className={`${cx}__sidebarEmpty`}>
											<Upload size={30} strokeWidth={1.3} />
											<span>No imports yet</span>
										</div>
									) : (
										registriesUploadRunList.map((run) => {
											const processing = isProcessing(run);
											const pct = progressPercent(run);
											const selected = selectedRunId === run.id;
											return (
												<button
													type='button'
													key={run.id}
													className={`${cx}__runItem ${
														selected ? `${cx}__runItem--selected` : ''
													} ${processing ? `${cx}__runItem--processing` : ''}`}
													onClick={() => setSelectedRunId(run.id)}
												>
													<div className={`${cx}__runIcon`}>
														{processing ? (
															<Loader2
																size={15}
																className={`${cx}__spinIcon`}
															/>
														) : run.failureCount > 0 ? (
															<XCircle size={15} />
														) : (
															<CheckCircle size={15} />
														)}
													</div>
													<div className={`${cx}__runContent`}>
														<div className={`${cx}__runTitle`}>
															<span>{run.title}</span>
															<ChevronRight size={15} />
														</div>
														<div className={`${cx}__runMeta`}>
															<span>{formatDate(run.createTimeStamp)}</span>
															<span>•</span>
															<span>{run.totalItems} rows</span>
														</div>
														{processing && (
															<div className={`${cx}__runProgress`}>
																<div className={`${cx}__runProgressTrack`}>
																	<div
																		className={`${cx}__runProgressFill`}
																		style={{
																			width: `${pct}%`,
																		}}
																	/>
																</div>
																<span>
																	{run.successCount + run.failureCount} /{' '}
																	{run.totalItems}
																</span>
															</div>
														)}
														{!processing && (
															<div className={`${cx}__runStats`}>
																<span className={`${cx}__successStat`}>
																	<CheckCircle size={11} />
																	{run.successCount}
																</span>
																<span className={`${cx}__failureStat`}>
																	<XCircle size={11} />
																	{run.failureCount}
																</span>
															</div>
														)}
													</div>
												</button>
											);
										})
									)}
								</div>
							</aside>
							<section className={`${cx}__detailsPanel`}>
								{selectedRun ? (
									<>
										<div className={`${cx}__detailsHeader`}>
											<div className={`${cx}__detailsTitle`}>
												<div>
													<h2>{selectedRun.title}</h2>
													<p>
														{formatDate(selectedRun.createTimeStamp)} •{' '}
														{selectedRun.totalItems} rows
													</p>
												</div>
												{isProcessing(selectedRun) ? (
													<span className={`${cx}__processingBadge`}>
														<Loader2 size={12} className={`${cx}__spinIcon`} />
														Processing
													</span>
												) : (
													<button
														type='button'
														className={`${cx}__downloadRunFile`}
														onClick={() =>
															downloadUploadRunFile(selectedRun.id)
														}
														title='Download original file'
													>
														<Download size={15} />
														Download file
													</button>
												)}
											</div>
											<div className={`${cx}__detailsStats`}>
												<div className={`${cx}__summarySuccess`}>
													<CheckCircle size={14} />
													<strong>{selectedRun.successCount}</strong>
													<span>Success</span>
												</div>
												<div className={`${cx}__summaryFailure`}>
													<XCircle size={14} />
													<strong>{selectedRun.failureCount}</strong>
													<span>Failed</span>
												</div>
												{!isProcessing(selectedRun) &&
													selectedRun.failureBase64Data &&
													selectedRun.failureCount > 0 && (
														<button
															className={`${cx}__downloadFailureBtn`}
															onClick={() =>
																downloadFailureData(selectedRun.id)
															}
														>
															<Download size={13} />
															Failed CSV
														</button>
													)}
												{!isProcessing(selectedRun) &&
													selectedRun.successCount > 0 && (
														<button
															className={`${cx}__downloadSuccessBtn`}
															onClick={() =>
																downloadSuccessData(selectedRun.id)
															}
														>
															<Download size={13} />
															Success CSV
														</button>
													)}
											</div>
										</div>
										{isProcessing(selectedRun) && (
											<div className={`${cx}__detailsProgress`}>
												<div className={`${cx}__progressTrack`}>
													<div
														className={`${cx}__progressFill`}
														style={{
															width: `${progressPercent(selectedRun)}%`,
														}}
													/>
												</div>
												<span>
													{selectedRun.successCount + selectedRun.failureCount}{' '}
													of {selectedRun.totalItems} rows processed
												</span>
											</div>
										)}
										<div className={`${cx}__rowDetails`}>
											<RegistriesImportRow
												key={selectedRun.id}
												item={selectedRun}
												cachedDetails={jobDetailsCache[selectedRun.id]}
												setCache={(jobs) =>
													handleJobDetailsCache(selectedRun.id, jobs)
												}
											/>
										</div>
									</>
								) : (
									<div className={`${cx}__noSelection`}>
										<Clock3 size={40} strokeWidth={1.25} />
										<h3>Select an import</h3>
										<p>
											Select an import from the list to view its row-level
											results.
										</p>
									</div>
								)}
							</section>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}